import { useSelector } from "react-redux";
import { usePriceDropPredictionDetails } from "@apac/hooks";
import {
  Dealness,
  type Intervals,
  type Prediction,
} from "@b2bportal/air-shopping-api";
import { getPrediction, getTripCategory } from "@b2bportal/core-flights";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import { amountToFiatPrice, noFractionOptions } from "@/utilities";

export interface UsePricePredictionDialogReturn {
  context: {
    shouldWait: boolean;
    prediction: Prediction;
    intervals: Intervals;
    isPDPEligible: boolean;
    priceDropAmountPrice: string;
    currentLowestPrice: string;
    forecastMaxPrice: string;
    forecastMinPrice: string;
    lowestPriceSuffix: string;
  };
}

export const usePricePredictionDialog = (): UsePricePredictionDialogReturn => {
  const tripCategory = useSelector(getTripCategory);
  const prediction = useSelector(getPrediction);
  const { t, formatFiatCurrency } = useI18nContext();

  const shouldWait = prediction.dealness === Dealness.wait;

  const intervals = prediction?.predictionCopy?.intervals;

  const { isPDPEligible, priceDropAmount } = usePriceDropPredictionDetails();

  const lowestPriceSuffix =
    tripCategory === TripCategory.ROUND_TRIP
      ? t("pricePrediction.lowestPriceRoundTripSuffix")
      : t("pricePrediction.lowestPriceOneWaySuffix");

  return {
    context: {
      shouldWait,
      prediction,
      intervals,

      priceDropAmountPrice: formatFiatCurrency(
        amountToFiatPrice(priceDropAmount?.amount),
        noFractionOptions
      ),
      isPDPEligible,

      currentLowestPrice: formatFiatCurrency(
        prediction?.lowestPrice?.fiat,
        noFractionOptions
      ),

      forecastMaxPrice: formatFiatCurrency(
        amountToFiatPrice(prediction?.pricePrediction?.maxPrice?.amount),
        noFractionOptions
      ),
      forecastMinPrice: formatFiatCurrency(
        amountToFiatPrice(prediction?.pricePrediction?.minPrice?.amount),
        noFractionOptions
      ),
      lowestPriceSuffix,
    },
  };
};
